import { Badge } from '@/components/ui/badge';
import { TrendingUp, Flame, Zap } from 'lucide-react';
import type { ShortageSignal } from '@/hooks/useShortageSignals';

interface ShortageSignalBadgeProps {
  signal: ShortageSignal | null | undefined;
  size?: 'sm' | 'md';
  showDetails?: boolean;
}

const ShortageSignalBadge = ({ signal, size = 'sm', showDetails = false }: ShortageSignalBadgeProps) => {
  if (!signal || signal.shortage_level === 'normal') return null;

  const config = {
    'critical-shortage': {
      label: 'Critical Shortage',
      className: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300 border-red-200 dark:border-red-800',
      icon: Flame,
    },
    'high-demand': {
      label: 'High Demand',
      className: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300 border-orange-200 dark:border-orange-800',
      icon: Zap,
    },
    'moderate-demand': {
      label: 'In Demand',
      className: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300 border-amber-200 dark:border-amber-800',
      icon: TrendingUp,
    },
  }[signal.shortage_level];

  if (!config) return null;

  const Icon = config.icon;

  return (
    <div className="inline-flex items-center gap-1.5">
      <Badge
        variant="outline"
        className={`${config.className} ${size === 'sm' ? 'text-xs px-2 py-0.5' : 'text-sm px-3 py-1'} font-medium`}
      >
        <Icon className={size === 'sm' ? 'h-3 w-3 mr-1' : 'h-4 w-4 mr-1.5'} />
        {config.label}
      </Badge>
      {showDetails && signal.pct_change > 0 && (
        <span className="text-xs text-muted-foreground">
          +{signal.pct_change}% projected growth
        </span>
      )}
    </div>
  );
};

export default ShortageSignalBadge;
